<template>
    <div class="monitor-page">
        <!-- 顶部操作栏 -->
        <div class="top-bar">
            <div class="bar-left">
                <el-button type="primary" size="small" @click="handleAddChannel">添加通道</el-button>
                <el-button size="small" @click="handleRefresh">刷新</el-button>

                <!-- 功能间筛选 -->
                <el-select v-model="queryParams.room" placeholder="按功能间筛选" size="small" clearable
                    style="width:180px; margin-left:10px;" @change="fetchChannelList">
                    <el-option v-for="item in roomList" :key="item.id" :label="item.name" :value="item.name" />
                </el-select>
            </div>
            <div class="bar-right">
                <el-button :type="viewMode === 'grid' ? 'primary' : 'default'" size="small"
                    @click="viewMode = 'grid'">视图</el-button>
                <el-button :type="viewMode === 'list' ? 'primary' : 'default'" size="small"
                    @click="viewMode = 'list'">列表</el-button>
            </div>
        </div>

        <!-- 内容区域：网格视图 -->
        <div v-if="viewMode === 'grid'" class="monitor-grid">
            <div v-for="channel in channelList" :key="channel.id" class="video-card">
                <div class="video-player">
                    <video ref="videoPlayerRefs" :id="`video-${channel.id}`" class="flv-video" controls muted></video>
                    <div class="play-overlay" v-if="!channel.playing && !channel.loading" @click="playVideo(channel)">
                        <i class="el-icon-video-play"></i>
                    </div>
                </div>
                <div class="video-info">
                    <span class="status online">●</span>
                    <span class="name">{{ channel.name }}</span>
                    <div class="ops">
                        <span class="name">{{ channel.room }}</span>
                        <i :class="channel.playing ? 'el-icon-video-pause' : 'el-icon-video-play'" title="播放/暂停"
                            @click.stop="togglePlay(channel)"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- 内容区域：列表视图 -->
        <div v-else class="monitor-list">
            <el-table :data="channelList" style="width: 100%" size="small">
                <el-table-column prop="name" label="名称" width="150" />
                <el-table-column prop="url" label="流地址">
                    <template #default="{ row }">
                        <el-tooltip :content="row.url" placement="top">
                            <span class="url-text">{{ row.url }}</span>
                        </el-tooltip>
                    </template>
                </el-table-column>
                <el-table-column prop="room" label="功能间" width="150" />
                <el-table-column prop="position" label="位置" width="150" />

                <el-table-column label="操作" width="150">
                    <template #default="{ row }">
                        <el-button size="mini" type="text" @click="handlePreview(row)">{{ row.playing ? '暂停' : '播放'
                            }}</el-button>
                        <el-dropdown @command="(cmd) => handleMoreCmd(cmd, row)">
                            <el-button size="mini" type="text">更多</el-button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="edit">编辑</el-dropdown-item>
                                <el-dropdown-item command="delete">删除</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 监控预览弹窗 -->
        <el-dialog title="监控预览" :visible.sync="previewVisible" width="80%" append-to-body :close-on-click-modal="false"
            @close="stopPreviewVideo">
            <div class="preview-content">
                <div class="preview-video">
                    <video ref="previewVideoRef" id="preview-video" class="flv-video-preview" controls muted></video>
                </div>
                <div class="preview-info">
                    <p><strong>通道名称：</strong>{{ previewChannel.name }}</p>
                    <p><strong>流地址：</strong>{{ previewChannel.url }}</p>
                    <p><strong>功能间：</strong>{{ previewChannel.room || '-' }}</p>
                    <p><strong>状态：</strong>{{ previewChannel.playing ? '播放中' : '已暂停' }}</p>
                </div>
            </div>
            <template #footer>
                <el-button @click="stopPreviewVideo">暂停</el-button>
                <el-button @click="playPreviewVideo">播放</el-button>
                <el-button @click="previewVisible = false">关闭</el-button>
            </template>
        </el-dialog>

        <!-- 新增/编辑通道弹窗 -->
        <el-dialog :title="isEdit ? '编辑监控通道' : '添加监控通道'" :visible.sync="channelDialogVisible" width="500px"
            append-to-body :close-on-click-modal="false" @close="resetChannelForm">
            <el-form ref="channelFormRef" :model="channelForm" :rules="channelRules" label-width="80px">
                <el-form-item label="通道名称" prop="name">
                    <el-input v-model="channelForm.name" placeholder="请输入通道名称（如：金佰特后厨1）" clearable></el-input>
                </el-form-item>
                <el-form-item label="流地址" prop="address">
                    <el-input v-model="channelForm.address"
                        placeholder="请输入FLV流地址（如：http://xxx.xxx.xxx.xxx/preview/1.live.flv）" clearable type="textarea"
                        rows="3"></el-input>
                </el-form-item>

                <!-- 功能间下拉框 -->
                <el-form-item label="功能间" prop="room">
                    <el-select v-model="channelForm.room" placeholder="请选择功能间" clearable style="width:100%">
                        <el-option v-for="item in roomList" :key="item.id" :label="item.name" :value="item.name" />
                    </el-select>
                </el-form-item>

                <el-form-item label="位置" prop="position">
                    <el-input v-model="channelForm.position" placeholder="请输入位置" clearable></el-input>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="channelDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitChannelForm">{{ isEdit ? '确定修改' : '确定添加' }}</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { getCheckData, add, edit, delmonitor } from '@/api/monitor'
import flvjs from 'flv.js';
import { getRoomList } from "@/api/function";

export default {
    data() {
        return {
            viewMode: 'grid',
            channelList: [],
            roomList: [],
            queryParams: { room: '' },
            previewVisible: false,
            previewChannel: {},
            playerInstances: {},
            previewPlayer: null,
            channelDialogVisible: false,
            isEdit: false,
            channelForm: {
                id: '',
                name: '',
                address: '',
                position: '',
                room: ''
            },
            channelRules: {
                name: [
                    { required: true, message: '请输入通道名称', trigger: 'blur' },
                    { min: 2, max: 50, message: '通道名称长度在 2 到 50 个字符', trigger: 'blur' }
                ],
                address: [
                    { required: true, message: '请输入FLV流地址', trigger: 'blur' },
                    { pattern: /\.flv$/, message: '请输入有效的FLV流地址（以.flv结尾）', trigger: 'blur' }
                ]
            }
        };
    },
    mounted() {
        this.loadRoomList().then(() => {
            // 接收路由参数 roomName，自动筛选
            const roomName = this.$route.query.roomName || this.$route.params.roomName;
            if (roomName) {
                this.queryParams.room = roomName;
            }
            this.fetchChannelList();
        });
    },
    beforeDestroy() {
        Object.keys(this.playerInstances).forEach(key => {
            if (this.playerInstances[key]) {
                this.playerInstances[key].destroy();
            }
        });
        if (this.previewPlayer) {
            this.previewPlayer.destroy();
        }
    },
    methods: {
        // 加载功能间
        async loadRoomList() {
            try {
                const res = await getRoomList({ userId: localStorage.getItem('userId') || '' });
                this.roomList = (res.data?.list || res.data || []).map(i => ({
                    id: i.id || i.roomId,
                    name: i.name || i.roomName
                }));
            } catch (e) {
                console.error('加载功能间失败', e);
            }
        },

        // 获取通道
        async fetchChannelList() {
            try {
                const res = await getCheckData(this.queryParams);
                if (res.code === 200 || res.success) {
                    this.channelList = (res.data || []).map(item => ({
                        id: item.id || item.channelId,
                        name: item.name || item.channelName,
                        url: item.url || item.streamUrl || item.address,
                        room: item.room || '',
                        position: item.position || '',
                        enabled: false,
                        playing: false,
                        loading: false,
                        ...item
                    }));
                }
            } catch (error) {
                console.error('获取通道失败', error);
            }
        },

        // ========== 刷新：清空筛选框 ==========
        handleRefresh() {
            this.channelList.forEach(c => c.playing && this.stopVideo(c));
            // 清空筛选
            this.queryParams.room = '';
            this.fetchChannelList().then(() => {
                this.$message.success('刷新成功');
            });
        },

        // 提交
        async submitChannelForm() {
            this.$refs.channelFormRef.validate(async (valid) => {
                if (!valid) return;
                try {
                    const formData = {
                        name: this.channelForm.name,
                        address: this.channelForm.address,
                        position: this.channelForm.position,
                        room: this.channelForm.room
                    };
                    let res;
                    if (this.isEdit) {
                        formData.id = this.channelForm.id;
                        res = await edit(formData);
                    } else {
                        res = await add(formData);
                    }
                    if (res.code === 200) {
                        this.$message.success(this.isEdit ? '修改成功' : '添加成功');
                        this.channelDialogVisible = false;
                        this.fetchChannelList();
                    }
                } catch (error) {
                    console.error('提交失败', error);
                }
            });
        },

        // 删除
        async deleteChannel(row) {
            try {
                const res = await delmonitor({ id: row.id });
                if (res.code === 200) {
                    this.$message.success('删除成功');
                    this.stopVideo(row);
                    this.fetchChannelList();
                }
            } catch (error) {
                console.error('删除失败', error);
            }
        },

        handleAddChannel() {
            this.isEdit = false;
            this.channelDialogVisible = true;
            this.resetChannelForm();
        },
        handleEditChannel(row) {
            this.isEdit = true;
            this.channelDialogVisible = true;
            this.channelForm = {
                id: row.id,
                name: row.name,
                address: row.url,
                position: row.position,
                room: row.room || ''
            };
        },
        resetChannelForm() {
            this.channelForm = { id: '', name: '', address: '', position: '', room: '' };
            if (this.$refs.channelFormRef) {
                this.$refs.channelFormRef.resetFields();
            }
        },

        // 播放
        playVideo(channel) {
            if (!channel.url?.endsWith('.flv')) return;
            channel.loading = true;
            if (this.playerInstances[channel.id]) {
                this.playerInstances[channel.id].destroy();
            }
            if (flvjs.isSupported()) {
                const video = document.getElementById(`video-${channel.id}`);
                const player = flvjs.createPlayer({
                    type: 'flv',
                    url: channel.url,
                    isLive: true,
                    hasAudio: false,
                    hasVideo: true
                });
                player.attachMediaElement(video);
                player.on('ready', () => {
                    channel.playing = true;
                    channel.loading = false;
                });
                player.on('error', (e) => {
                    console.warn('播放错误', e);
                    channel.playing = false;
                    channel.loading = false;
                });
                player.load();
                player.play().catch(() => { });
                this.playerInstances[channel.id] = player;
            }
        },
        stopVideo(channel) {
            if (this.playerInstances[channel.id]) {
                this.playerInstances[channel.id].destroy();
                delete this.playerInstances[channel.id];
            }
            channel.playing = false;
            channel.loading = false;
        },
        togglePlay(channel) {
            channel.playing ? this.stopVideo(channel) : this.playVideo(channel);
        },

        playPreviewVideo() {
            if (this.previewPlayer) this.previewPlayer.destroy();
            const video = document.getElementById('preview-video');
            const player = flvjs.createPlayer({
                type: 'flv',
                url: this.previewChannel.url,
                isLive: true,
                hasAudio: false
            });
            player.attachMediaElement(video);
            player.load();
            player.play().catch(() => { });
            this.previewPlayer = player;
            this.previewChannel.playing = true;
        },
        stopPreviewVideo() {
            if (this.previewPlayer) {
                this.previewPlayer.destroy();
                this.previewPlayer = null;
            }
            this.previewChannel.playing = false;
        },

        handlePreview(channel) {
            this.previewChannel = { ...channel };
            this.previewVisible = true;
            this.$nextTick(() => this.playPreviewVideo());
        },
        handleMoreCmd(cmd, row) {
            if (cmd === 'edit') this.handleEditChannel(row);
            if (cmd === 'delete') {
                this.$confirm('确定删除？', '提示', { type: 'warning' }).then(() => this.deleteChannel(row));
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.monitor-page {
    width: 100%;
    min-height: 100vh;
    background-color: #fff;
}

.top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    border-bottom: 1px solid #ebeef5;
}

.monitor-grid {
    display: flex;
    flex-wrap: wrap;
    padding: 20px;
    gap: 20px;
}

.video-card {
    width: calc(25% - 15px);
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }

    .video-player {
        position: relative;
        width: 100%;
        height: 220px;
        overflow: hidden;

        .flv-video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            background-color: #000;
        }

        .play-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
            cursor: pointer;

            i {
                font-size: 36px;
                color: #fff;
            }
        }
    }

    .video-info {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        font-size: 14px;

        .status {
            margin-right: 6px;
            font-size: 12px;
        }

        .status.online {
            color: #52c41a;
        }

        .name {
            flex: 1;
            color: #333;
        }

        .ops {
            display: flex;
            gap: 8px;
            color: #666;

            i {
                cursor: pointer;

                &:hover {
                    color: #409EFF;
                }
            }
        }
    }
}

.monitor-list {
    padding: 20px;
}

.url-text {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
}

.preview-content {
    .preview-video {
        width: 100%;
        margin-bottom: 16px;

        .flv-video-preview {
            width: 100%;
            height: 480px;
            background-color: #000;
            border-radius: 4px;
        }
    }

    .preview-info {
        p {
            margin: 8px 0;
            font-size: 14px;
            color: #666;
        }
    }
}

@media (max-width: 1200px) {
    .video-card {
        width: calc(33.33% - 14px);
    }
}

@media (max-width: 768px) {
    .video-card {
        width: calc(50% - 10px);

        .video-player {
            height: 180px;
        }
    }

    .preview-content .flv-video-preview {
        height: 320px;
    }
}
</style>